import countriesList from '@config/countries-list';
import languagesList from '@config/languages-list';
import AnalystSelector from '@molecules/AnalystSelector';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { People } from '@organisms';
import { TextInput, IntroMessagesDropdown } from '@molecules';
import { FlexContainer, Whitespace } from '@layout';
import { Button, CheckboxBlock, Dropdown, H2 } from '@atoms';
import 'url-search-params-polyfill';
import messages from './messages';

const StyledLabel = styled.div`
  box-sizing: border-box;
  color: rgb(175, 175, 175);
  cursor: default;
  display: block;
  font-family: Lato, sans-serif;
  font-size: 12px;
  padding-top: 3px;
`;

class OrganizationInformationForm extends PureComponent {
  static propTypes = {
    windowOpen: PropTypes.func.isRequired,
    winndowClose: PropTypes.func.isRequired,
    allRules: PropTypes.array,
    fullAnalystsList: PropTypes.array,
    introMessages: PropTypes.array,
  };

  componentWillMount() {
    !this.props.editMode && this.props.windowOpen();
    this.props.loadAnalysts();
  }

  componentWillUnmount() {
    this.props.winndowClose();
  }

  handleChange = (event) => {
    this.props.updateOrganizationField({
      [event.target.name]: event.target.value,
    });
  };

  handleDropdownChange = (payload) => {
    this.props.updateOrganizationField(payload);
  };

  handleAnalyticSelectAdd = (item, index) => {
    const {
      organizationsEditForm: { users },
    } = this.props;
    const itemFounded = index > -1;

    itemFounded &&
      this.props.updateOrganizationField({
        users: [...users, item],
      });
  };

  handleAnalyticSelectRemove = (item) => {
    const {
      organizationsEditForm: { users },
    } = this.props;

    this.props.updateOrganizationField({
      users: _.differenceWith(users, [item], _.isEqual),
    });
  };

  handleUpdateInput = (searchText) => {
    this.props.loadAnalysts(searchText);
  };

  handleRuleCheckbox = (e) => {
    e && e.stopPropagation();
    const ruleCode = e.target.id;
    const { allRules, organizationsEditForm } = this.props;
    const rule = allRules.find(({ code }) => code === ruleCode);

    if (!rule) {
      console.error(`Rule ${ruleCode} not found`);
      return;
    }

    if (!organizationsEditForm.rules.includes(rule.id)) {
      this.props.updateOrganizationField({
        rules: [...organizationsEditForm.rules, rule.id],
      });
      return;
    }
    this.props.updateOrganizationField({
      rules: organizationsEditForm.rules.filter((id) => id !== rule.id),
    });
  };

  isRuleEnabled = (ruleCode) => {
    const { allRules, organizationsEditForm } = this.props;
    const rule = allRules.find(({ code }) => code === ruleCode);

    if (!rule) {
      return false;
    }
    return organizationsEditForm.rules.includes(rule.id);
  };

  render() {
    const {
      organizationsEditForm: {
        name,
        company,
        rules: orgRules,
        country,
        lang,
        isActive,
        users,
        attendees,
        introMessageId,
      },
      fullAnalystsList,
      numbersByCountries,
      addAttendee,
      removeAttendee,
      allRules,
      introMessages,
    } = this.props;
    const countriesOptions = Object.keys(countriesList).map((country) => ({
      label: country,
      value: country,
    }));
    const languagesOptions = Object.keys(languagesList).map((language) => ({
      label: language,
      value: language,
    }));
    const connectionOptions = [
      {
        label: 'Active',
        value: true,
      },
      {
        label: 'Deactivated',
        value: false,
      },
    ];

    const rules = allRules.map(({ code, description, id }) => (
      <div key={id}>
        <CheckboxBlock
          id={code}
          label={
            <FormattedMessage id={`CLIENT.rule-${code}`} defaultMessage={description} />
          }
          checked={this.isRuleEnabled(code)}
          onCheck={this.handleRuleCheckbox}
        />
        <Whitespace size="1rem" />
      </div>
    ));

    return (
      <FlexContainer vertical>
        <H2>
          <FormattedMessage {...messages.information} />
        </H2>
        <Whitespace size="1rem" />
        <FlexContainer gap="1rem">
          <FlexContainer vertical style={{ minWidth: '300px' }}>
            <TextInput
              underlined
              id="company"
              label={<FormattedMessage {...messages.company} />}
              value={company}
              onChange={this.handleChange}
              name="company"
            />
            <Whitespace size="1rem" />
            <div>
              <StyledLabel>
                <FormattedMessage {...messages.language} />
              </StyledLabel>
              <Dropdown
                id="lang"
                name="lang"
                onChange={(value) => {
                  this.handleDropdownChange({ lang: value });
                }}
                options={languagesOptions}
                value={lang}
                className="language"
                className="language-dropdown"
              />
            </div>
          </FlexContainer>
          <FlexContainer vertical style={{ minWidth: '300px' }}>
            <TextInput
              underlined
              id="organizationName"
              label={<FormattedMessage {...messages.name} />}
              value={name}
              onChange={this.handleChange}
              name="name"
            />
            <Whitespace size="1rem" />
            <div>
              <StyledLabel>
                <FormattedMessage {...messages.connectionStatus} />
              </StyledLabel>
              <Dropdown
                id="isActive"
                name="isActive"
                className="activity-status"
                onChange={(value) => {
                  this.handleDropdownChange({ isActive: value });
                }}
                options={connectionOptions}
                value={isActive ? 'Active' : 'Deactivated'}
                className="isActive-dropdown"
              />
            </div>
          </FlexContainer>
          <FlexContainer vertical style={{ minWidth: '300px' }}>
            <div>
              <StyledLabel>
                <FormattedMessage {...messages.country} />
              </StyledLabel>
              <Dropdown
                onChange={(value) => {
                  this.handleDropdownChange({ country: value });
                }}
                options={countriesOptions}
                value={country}
                className="country-dropdown"
                name="country"
              />
            </div>
          </FlexContainer>
        </FlexContainer>
        <Whitespace size="1.5rem" />
        <H2>
          <FormattedMessage {...messages.settings} />
        </H2>
        {rules}
        <People
          people={attendees}
          disabledFields={['type']}
          setPersonField={this.props.updateOrganizationAttendee}
          numbersByCountries={numbersByCountries}
          removePerson={removeAttendee}
          addPerson={addAttendee}
          header={<FormattedMessage {...messages.requiredClientUser} />}
        />
        <div style={{ maxWidth: '573px' }}>
          <Whitespace size="1.5rem" />
          <H2>
            <FormattedMessage {...messages.companyAnalyst} />
          </H2>
          <Whitespace size="1rem" />
          <AnalystSelector
            list={fullAnalystsList}
            selectedList={users}
            onAdd={this.handleAnalyticSelectAdd}
            onRemove={this.handleAnalyticSelectRemove}
            onUpdateInput={this.handleUpdateInput}
          />
        </div>
        <Whitespace size="1rem" />
        <H2>
          <FormattedMessage {...messages.introMessage} />
        </H2>
        <IntroMessagesDropdown
          value={introMessageId}
          onChange={this.handleChange}
          introMessages={introMessages}
        />
        <Whitespace size="1.5rem" />
      </FlexContainer>
    );
  }
}

export default OrganizationInformationForm;



// WEBPACK FOOTER //
// ./components/templates/OrganizationInformationForm/OrganizationInformationForm.js